<template>
    <benefits-modal name="faq" ref="modal" @open="loadFaq">
        <main class="faq">
            <loader v-if="loading" />

            <template v-else-if="!items.length">
                <h1 class="faq-title">{{ title }}</h1>
                <p class="faq-empty" v-lang="'dashboard-faqEmpty'"></p>
            </template>

            <template v-else>
                <template v-if="openItem">
                    <div class="faq-answer">
                        <button type="button" class="faq-answer-question" @click="openIndex = null">
                            {{ openItem.question }}
                        </button>
                        <rich-text class="faq-answer-body" :content="openItem.answer" />
                    </div>

                    <h2 class="faq-others" v-lang="'dashboard-faqOthers'"></h2>
                </template>

                <template v-else>
                    <h1 class="faq-title">{{ title }}</h1>
                    <img v-if="image" :src="image.url" :alt="image.name" class="faq-image" />
                    <rich-text v-if="intro" class="faq-intro" :content="intro" />
                </template>

                <div class="faq-search">
                    <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="#384250" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                        <circle cx="11" cy="11" r="8"></circle>
                        <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                    </svg>
                    <input type="search" v-model="query" :placeholder="t('dashboard-faqSearch')" />
                </div>

                <p class="faq-empty" v-if="!visibleItems.length" v-lang="'dashboard-faqNoResults'"></p>

                <ul class="faq-list" v-else>
                    <li v-for="question in visibleItems" :key="question.index">
                        <button type="button" class="faq-item" @click="select(question.index)">
                            <span>{{ question.question }}</span>
                            <svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="#384250" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                                <polyline points="9 18 15 12 9 6"></polyline>
                            </svg>
                        </button>
                    </li>
                </ul>
            </template>
        </main>
    </benefits-modal>
</template>

<script>
import Api from "../../api";
import config from "../../config";
import loader from "../../shared/loader";
import BenefitsModal from "../../shared/modal-shell";
import RichText from "../../shared/rich-text";
import { htmlToText } from "../../helpers/sanitize-html";

const fold = (value) =>
    String(value || "")
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .toLowerCase();

export default {
    name: "FaqModal",
    components: { loader, BenefitsModal, RichText },
    data() {
        return {
            items: [],
            image: null,
            title: "",
            intro: "",
            query: "",
            openIndex: null,
            loading: true,
        };
    },
    computed: {
        previewFaq() {
            const program = this.$store.state.place.benefit_program || {};
            return program.faq || {};
        },
        openItem() {
            return this.openIndex === null ? null : this.items[this.openIndex] || null;
        },
        visibleItems() {
            const term = fold(this.query).trim();

            const pool = this.items
                .map((question, index) => ({ ...question, index }))
                .filter((question) => question.index !== this.openIndex);

            if (!term) {
                return pool;
            }

            return pool
                .map((question) => {
                    const inQuestion = fold(question.question).indexOf(term) !== -1;
                    const inAnswer = fold(htmlToText(question.answer)).indexOf(term) !== -1;

                    return { item: question, rank: inQuestion ? 0 : inAnswer ? 1 : -1 };
                })
                .filter((entry) => entry.rank !== -1)
                .sort((a, b) => a.rank - b.rank)
                .map((entry) => entry.item);
        },
    },
    watch: {
        previewFaq: {
            deep: true,
            handler(faq) {
                if (config.isPreview) {
                    this.applyFaq(faq);
                }
            },
        },
    },
    methods: {
        reset() {
            this.items = [];
            this.image = null;
            this.title = this.t("dashboard-faqTitle");
            this.intro = "";
        },
        applyFaq(faq) {
            const source = faq || {};

            this.items = source.items || [];
            this.title = source.showTitle !== false && source.title ? source.title : this.t("dashboard-faqTitle");
            this.image = source.showImage !== false ? source.image || null : null;
            this.intro = source.showIntro !== false ? source.intro || "" : "";
        },
        loadFaq() {
            this.openIndex = null;
            this.query = "";

            if (config.isPreview) {
                this.applyFaq(this.previewFaq);
                this.loading = false;
                return;
            }

            this.loading = true;

            Api.get("place/" + window.PLACE_SLUG + "/benefits/faq")
                .then((response) => {
                    this.applyFaq((response.body || {}).faq);
                })
                .catch(() => {
                    this.reset();
                })
                .then(() => {
                    this.loading = false;
                });
        },
        select(index) {
            this.openIndex = index;
            this.query = "";
            this.scrollToTop();
        },
        scrollToTop() {
            this.$refs.modal.scrollToTop();
        },
    },
};
</script>

<style lang="scss">
.faq {
  flex: 1 1 auto;
  width: 100%;
  max-width: 30rem;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding: 1rem 1rem 3rem;

  @media (min-width: 48rem) {
    max-width: 37.5rem;
    gap: 1.5rem;
    padding: 3rem 1rem;
  }
}

.faq-title {
  margin: 0;
  font-size: clamp(1.25rem, 6vw, 1.5rem);
  font-weight: 400;
  line-height: 1.3;
  color: #1f2a37;
  text-align: center;
}

.faq-image {
  width: 100%;
  border-radius: 1rem;
  display: block;
}

.faq-answer {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-bottom: clamp(1.5rem, 12vw, 4rem);
}

.faq-answer-question {
  padding: 0;
  border: none;
  background: transparent;
  font-size: clamp(1.125rem, 5vw, 1.25rem);
  font-weight: 400;
  line-height: 1.3;
  color: #1f2a37;
  text-align: left;
  cursor: pointer;
  overflow-wrap: anywhere;
}

.faq-answer-body {
  font-size: 0.875rem;
  line-height: 1.6;
  color: #4d5761;
}

.faq-intro {
  font-size: 0.875rem;
  line-height: 1.6;
  color: #4d5761;
  text-align: center;
}

.faq-others {
  margin: 0;
  font-size: 1rem;
  font-weight: 500;
  line-height: 1.3;
  color: #384250;
}

.faq-search {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem;
  border: 1px solid #e5e7eb;
  border-radius: 0.5rem;
  background: #f9fafb;
}

.faq-search svg {
  flex: 0 0 auto;
}

.faq-search input {
  flex: 1 1 auto;
  min-width: 0;
  border: none;
  outline: none;
  background: transparent;
  font-size: 0.875rem;
  color: #1f2a37;

  &::placeholder {
    color: #9da4ae;
  }
}

.faq-list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
}

.faq-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  width: 100%;
  padding: 1rem 0.5rem;
  border: none;
  border-bottom: 1px solid #d2d6db;
  background: transparent;
  font-size: 1rem;
  font-weight: 500;
  line-height: 1.4;
  color: #1f2a37;
  text-align: left;
  cursor: pointer;
  transition: background-color 0.2s;
  overflow-wrap: anywhere;

  &:hover {
    background: #f9fafb;
  }
}

.faq-item svg {
  flex: 0 0 auto;
}

.faq-empty {
  margin: 0;
  padding: 1.5rem 0;
  font-size: 0.875rem;
  color: #6c737f;
  text-align: center;
}
</style>
